import { Link, useNavigate } from "react-router";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/providers/ThemeToggle";
import { MegaMenu } from "./MegaMenu";
import { ConsoleStatusRow } from "./ConsoleStatusRow";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Menu,
  User,
  LogOut,
  Building2,
  LayoutDashboard,
  MessageCircle,
  Shield,
  ArrowRight,
} from "lucide-react";
import { lazy, Suspense, useState, useRef, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { cn } from "@/lib/utils";
import { signOut, useAuthUser, useUserRoles } from "@/features/auth";

const NotificationBell = lazy(() =>
  import("@/features/candidate-dashboard/components/NotificationBell").then((m) => ({
    default: m.NotificationBell,
  }))
);
const MessageBell = lazy(() =>
  import("@/features/messaging/components/MessageBell").then((m) => ({
    default: m.MessageBell,
  }))
);

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isLoading: isAuthLoading } = useAuthUser();
  const { isAdmin, isEmployer } = useUserRoles(user?.id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isScrolled, scrollProgress } = useScrollDirection();

  const handleSignOut = async () => {
    const result = await signOut();

    if (!result.ok) {
      toast({
        title: "Sign out failed",
        description: result.errorMessage ?? "Please try again.",
        variant: "destructive",
      });
      return;
    }

    navigate("/");
  };

  const postJobLink = !user
    ? "/archive"
    : isEmployer
      ? "/post-job"
      : "/employer/access";

  const navRef = useRef<HTMLElement>(null);

  // Keep --nav-height CSS variable in sync with actual nav height
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty("--nav-height", `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <nav
      ref={navRef}
      data-nav
      className={cn(
        "sticky top-0 z-50 transition-shadow duration-300 bg-background/95 backdrop-blur",
        "pt-[env(safe-area-inset-top)]",
        isScrolled ? "shadow-sm shadow-black/5" : "shadow-none"
      )}
    >
      {/* Main nav row — fixed height, hairline border, near-opaque backdrop
          so long pages (jobs, signal) don't bleed card text through the nav. */}
      <div className="console-hairline border-b border-border/70">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between gap-4 h-14 lg:h-16">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <Link to="/" className="flex-shrink-0" aria-label="Rack & Role home">
                <Logo />
              </Link>

              {/* Desktop Mega Menu */}
              <MegaMenu postJobLink={postJobLink} />
            </div>

            {/* Desktop Right Side */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              {isAuthLoading ? (
                <div className="flex items-center gap-2" aria-label="Loading authentication status">
                  <Skeleton className="h-8 w-16 rounded-md" />
                  <Skeleton className="h-8 w-24 rounded-md" />
                </div>
              ) : user ? (
                <>
                  <Suspense fallback={null}>
                    <MessageBell userId={user.id} />
                    <NotificationBell userId={user.id} />
                  </Suspense>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full"
                        aria-label="Account menu"
                      >
                        <User className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-56 rounded-xl border-border/60 bg-popover/98 backdrop-blur-xl shadow-lg shadow-black/10 z-50"
                    >
                      <DropdownMenuLabel className="mono-eyebrow !font-mono">
                        Account
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-border/60" />
                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer">
                            <Shield className="mr-2 h-4 w-4 text-primary" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {isEmployer && (
                        <DropdownMenuItem asChild>
                          <Link to="/employer" className="cursor-pointer">
                            <Building2 className="mr-2 h-4 w-4" />
                            Employer Dashboard
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="cursor-pointer">
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/messages" className="cursor-pointer">
                          <MessageCircle className="mr-2 h-4 w-4" />
                          Messages
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/profile")}>
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border/60" />
                      <DropdownMenuItem onClick={handleSignOut}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link to="/auth">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 mono-eyebrow !text-[11px] !tracking-[0.16em] !font-mono text-foreground/70 hover:text-foreground"
                    >
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/archive" className="group">
                    <Button
                      size="sm"
                      className={cn(
                        "h-9 px-4 rounded-md gap-1.5 relative overflow-hidden",
                        "bg-foreground text-background hover:bg-foreground/90",
                        "mono-eyebrow !text-background !text-[11px] !tracking-[0.16em] !font-mono",
                        "shadow-sm shadow-black/10 transition-all"
                      )}
                    >
                      <span className="relative z-10">Archive notice</span>
                      <ArrowRight className="h-3.5 w-3.5 relative z-10 transition-transform group-hover:translate-x-0.5" />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-gradient-thermal opacity-90"
                      />
                    </Button>
                  </Link>
                </>
              )}
              <ThemeToggle />
            </div>

            {/* Mobile Right Side */}
            <div className="lg:hidden flex items-center gap-1.5">
              {user && (
                <Suspense fallback={null}>
                  <MessageBell userId={user.id} />
                  <NotificationBell userId={user.id} />
                </Suspense>
              )}
              <ThemeToggle />
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <button
                    className="p-2 rounded-md transition-all duration-300 ease-out hover:bg-foreground/[0.05] active:scale-95"
                    aria-label="Open menu"
                  >
                    <Menu className="h-6 w-6" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-[88vw] max-w-sm border-l-border/60 bg-background/98 backdrop-blur-xl p-0 flex flex-col h-[100dvh]"
                >
                  <SheetTitle className="sr-only">Site navigation</SheetTitle>

                  {/* Drawer status strip */}
                  <ConsoleStatusRow compact className="border-b shrink-0" />

                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-0.5">
                    <p className="mono-eyebrow mb-1.5">Discover</p>
                    <MobileLink to="/jobs" onNavigate={() => setMobileMenuOpen(false)}>
                      Browse roles
                    </MobileLink>
                    <MobileLink to="/companies" onNavigate={() => setMobileMenuOpen(false)}>
                      Companies
                    </MobileLink>
                    <MobileLink to="/signal" onNavigate={() => setMobileMenuOpen(false)}>
                      Signal feed
                    </MobileLink>
                    <MobileLink to="/learning" onNavigate={() => setMobileMenuOpen(false)}>
                      Learning hub
                    </MobileLink>
                    <MobileLink to="/learning/courses" onNavigate={() => setMobileMenuOpen(false)}>
                      Course catalog
                    </MobileLink>
                    <MobileLink to="/services" onNavigate={() => setMobileMenuOpen(false)}>
                      Career services
                    </MobileLink>
                    <MobileLink to="/talent" onNavigate={() => setMobileMenuOpen(false)}>
                      Talent Wall
                    </MobileLink>
                    <MobileLink
                      to="/premium"
                      onNavigate={() => setMobileMenuOpen(false)}
                      accent
                    >
                      AI tools · Free
                    </MobileLink>

                    <p className="mono-eyebrow mt-5 mb-1.5">Employers</p>
                    <MobileLink to="/employers" onNavigate={() => setMobileMenuOpen(false)}>
                      Employer solutions
                    </MobileLink>
                    <MobileLink to={postJobLink} onNavigate={() => setMobileMenuOpen(false)} accent>
                      Post a role
                    </MobileLink>

                    {user && !isAuthLoading && (
                      <>
                        <p className="mono-eyebrow mt-5 mb-1.5">Account</p>
                        {isAdmin && (
                          <MobileLink to="/admin" onNavigate={() => setMobileMenuOpen(false)}>
                            Admin
                          </MobileLink>
                        )}
                        {isEmployer && (
                          <MobileLink to="/employer" onNavigate={() => setMobileMenuOpen(false)}>
                            Employer dashboard
                          </MobileLink>
                        )}
                        <MobileLink to="/dashboard" onNavigate={() => setMobileMenuOpen(false)}>
                          Dashboard
                        </MobileLink>
                        <MobileLink to="/messages" onNavigate={() => setMobileMenuOpen(false)}>
                          Messages
                        </MobileLink>
                        <MobileLink to="/profile" onNavigate={() => setMobileMenuOpen(false)}>
                          Profile
                        </MobileLink>
                      </>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-border/60 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-2">
                    {isAuthLoading ? (
                      <Skeleton className="h-9 w-full rounded-md" aria-label="Loading authentication status" />
                    ) : user ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground"
                        onClick={() => {
                          handleSignOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                      </Button>
                    ) : (
                      <>
                        <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" size="sm" className="w-full">
                            Sign in
                          </Button>
                        </Link>
                        <Link to="/archive" onClick={() => setMobileMenuOpen(false)}>
                          <Button
                            size="sm"
                             variant="premium" className="w-full gap-1.5"
                          >
                            Archive notice
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>

                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Scroll progress indicator (thermal hairline) */}
        <div
          className="absolute bottom-0 left-0 h-[2px] bg-gradient-thermal transition-[width] duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
    </nav>
  );
};

interface MobileLinkProps {
  to: string;
  onNavigate: () => void;
  children: React.ReactNode;
  accent?: boolean;
}

function MobileLink({ to, onNavigate, children, accent }: MobileLinkProps) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        "block rounded-md px-3 py-2.5 text-[15px] font-medium transition-colors",
        accent
          ? "text-foreground hover:bg-foreground/[0.04] relative before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[2px] before:-translate-y-1/2 before:rounded-r-full before:bg-gradient-thermal"
          : "text-foreground/85 hover:text-foreground hover:bg-foreground/[0.04]"
      )}
    >
      {children}
    </Link>
  );
}
